import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Mail, Lock, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import BrandLogo from "../components/BrandLogo";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  
  // Reset password mode (when token is present)
  const token = searchParams.get("token");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    document.title = token ? "Reset Password | Smartorange HUB" : "Forgot Password | Smartorange HUB";
  }, [token]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("Check your email for reset instructions");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      setResetSuccess(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired reset link");
    } finally {
      setLoading(false);
    }
  };

  // Reset password form (when token is present)
  if (token) {
    if (resetSuccess) {
      return (
        <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
          <div className="fixed inset-0 bg-gradient-to-br from-orange-950/20 via-black to-black pointer-events-none" />
          <div className="relative z-10 w-full max-w-md">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-zinc-50 mb-2">Password Reset!</h2>
              <p className="text-zinc-400 mb-6">Your password has been successfully changed.</p>
              <Button
                onClick={() => navigate("/login")}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                Sign In
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
        <div className="fixed inset-0 bg-gradient-to-br from-orange-950/20 via-black to-black pointer-events-none" />
        <div className="fixed top-20 right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" />
        
        <div className="relative z-10 w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <BrandLogo className="h-10 mb-2" />
            <span className="text-sm font-semibold text-zinc-300">HUB</span>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8">
            <div className="mb-8 text-center">
              <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-6 h-6 text-orange-400" />
              </div>
              <h2 className="text-2xl font-bold text-zinc-50 mb-2">Set New Password</h2>
              <p className="text-zinc-500">Enter your new password below</p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-zinc-300">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-50 h-11"
                  required
                  minLength={6}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-zinc-300">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-50 h-11"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Request reset form
  if (sent) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
        <div className="fixed inset-0 bg-gradient-to-br from-orange-950/20 via-black to-black pointer-events-none" />
        <div className="relative z-10 w-full max-w-md">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-orange-400" />
            </div>
            <h2 className="text-2xl font-bold text-zinc-50 mb-2">Check Your Email</h2>
            <p className="text-zinc-400 mb-2">We've sent password reset instructions to:</p>
            <p className="text-zinc-50 font-medium mb-6">{email}</p>
            <p className="text-sm text-zinc-500 mb-6">
              Didn't receive the email? Check your spam folder or{" "}
              <button onClick={() => setSent(false)} className="text-orange-400 hover:underline">
                try again
              </button>
            </p>
            <Link to="/login" className="text-sm text-zinc-500 hover:text-orange-400">
              ← Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
      <div className="fixed inset-0 bg-gradient-to-br from-orange-950/20 via-black to-black pointer-events-none" />
      <div className="fixed top-20 right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <BrandLogo className="h-10 mb-2" />
          <span className="text-sm font-semibold text-zinc-300">HUB</span>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8">
          <div className="mb-8 text-center">
            <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-6 h-6 text-orange-400" />
            </div>
            <h2 className="text-2xl font-bold text-zinc-50 mb-2">Forgot Password?</h2>
            <p className="text-zinc-500">Enter your email and we'll send you a reset link</p>
          </div>

          <form onSubmit={handleRequestReset} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-zinc-800/50 border-zinc-700 text-zinc-50 h-11"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-zinc-500 hover:text-orange-400">
              ← Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
